import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../appUtils/axiosConfig.js";

const initialState = {
  isLoading: false,
  error: null,
  orderlist: [],
  singleorderlist: null,

  // ✅ Pagination state
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 1,
    totalItems: 0,
  },
};
// --- DIAGNOSTIC (temporary): report a failed checkout to the server so we can see
// exactly what the customer's browser received. Fire-and-forget, text/plain to avoid
// a CORS preflight, keepalive so it sends even if the page navigates. Remove after fix.
const reportCheckoutFailure = (info) => {
  try {
    fetch(`${process.env.REACT_APP_BASE_URL}/orders/_client_report.php`, {
      method: "POST",
      headers: { "Content-Type": "text/plain" },
      keepalive: true,
      body: JSON.stringify({
        t: new Date().toISOString(),
        href: window.location.href,
        ref: document.referrer,
        ua: navigator.userAgent,
        ...info,
      }),
    }).catch(() => {});
  } catch (e) {}
};

export const createOrder = createAsyncThunk(
  "orders/create_order",
  async (orderData, { dispatch, rejectWithValue }) => {
    const items = (orderData?.cart_items || []).map((i) => ({
      p: i.product_id,
      inv: i.inventory_id,
      q: i.quantity,
    }));
    const email = orderData?.address_data?.email;
    try {
      const response = await axios.post(`orders/create_order.php`, orderData, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response?.data || response.data.status !== "success") {
        reportCheckoutFailure({
          phase: "resolved-not-success",
          httpStatus: response?.status,
          dataType: typeof response?.data,
          raw: String(response?.request?.responseText ?? "").slice(0, 2000),
          data: (typeof response?.data === "string"
            ? response.data
            : JSON.stringify(response?.data)
          ).slice(0, 1500),
          method: orderData?.payment_method,
          items,
          email,
        });
      }
      return response?.data;
    } catch (error) {
      reportCheckoutFailure({
        phase: "threw",
        httpStatus: error?.response?.status ?? "no-response",
        raw: String(error?.request?.responseText ?? "").slice(0, 2000),
        data: JSON.stringify(error?.response?.data ?? null).slice(0, 1500),
        errMsg: error?.message,
        code: error?.code,
        method: orderData?.payment_method,
        items,
        email,
      });
      return rejectWithValue(
        error.response?.data?.message || "Failed to add order"
      );
    }
  }
);
// ================= GET ALL ORDERS (PAGINATED) =================
export const getOrderData = createAsyncThunk(
  "order/get_order",
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams({
        page,
        limit,
        ...filters,
      }).toString();

      const response = await axios.get(
        `orders/get_all_orders.php?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch orders"
      );
    }
  }
);


// ================= SINGLE ORDER =================
export const getSingleOrderData = createAsyncThunk(
  "order/get_single_order",
  async ({ orderId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `orders/get_single_order.php?order_id=${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch order"
      );
    }
  }
);

// ================= REMOVE ORDER =================
export const removeOrder = createAsyncThunk(
  "order/remove_order",
  async ({ order_id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `order/remove_from_order.php`,
        { order_id },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to remove order"
      );
    }
  }
);

// ================= UPDATE ORDER =================
export const updateOrder = createAsyncThunk(
  "order/update_order",
  async ({ order_id, orderData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `order/update_order.php?id=${order_id}`,
        orderData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update order"
      );
    }
  }
);

// ================= SLICE =================
const orderSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ===== GET ALL ORDERS =====
      .addCase(getOrderData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getOrderData.fulfilled, (state, action) => {
        state.isLoading = false;

        // 🔹 Orders
        state.orderlist = action.payload?.orders || [];

        // 🔹 Pagination (safe fallback)
        state.pagination = {
          page: action.payload?.pagination?.page || 1,
          limit: action.payload?.pagination?.limit || 10,
          totalPages: action.payload?.pagination?.totalPages || 1,
          totalItems: action.payload?.pagination?.totalItems || state.orderlist.length,
        };
      })
      .addCase(getOrderData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ===== SINGLE ORDER =====
      .addCase(getSingleOrderData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getSingleOrderData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.singleorderlist = action.payload;
      })
      .addCase(getSingleOrderData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ===== UPDATE ORDER =====
      .addCase(updateOrder.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateOrder.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export default orderSlice.reducer;
